/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { BulkPriceUpdateRequest } from '../models/bulk-price-update-request';
import { BulkPriceUpdateResponse } from '../models/bulk-price-update-response';
import { OriginalPricingResponse } from '../models/original-pricing-response';
import { PageProductSortimentPricingPageResponse } from '../models/page-product-sortiment-pricing-page-response';
import { ProductSortimentEntryPricingUpdateRequest } from '../models/product-sortiment-entry-pricing-update-request';
import { ProductSortimentPricingFilter } from '../models/product-sortiment-pricing-filter';
import { ProductSortimentsPricingRequest } from '../models/product-sortiments-pricing-request';
import { ProductSortimentsPricingResponse } from '../models/product-sortiments-pricing-response';

/**
 * Product sortiment pricing related endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class PricingService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation updateProductSortimentEntryPrices
   */
  static readonly UpdateProductSortimentEntryPricesPath = '/pricing/selected';

  /**
   * Updates list of product sortiment entries.
   *
   * Updates list of product sortiment entries with price values.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateProductSortimentEntryPrices()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProductSortimentEntryPrices$Response(
    params: {
      body: Array<ProductSortimentEntryPricingUpdateRequest>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PricingService.UpdateProductSortimentEntryPricesPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates list of product sortiment entries.
   *
   * Updates list of product sortiment entries with price values.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateProductSortimentEntryPrices$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProductSortimentEntryPrices(
    params: {
      body: Array<ProductSortimentEntryPricingUpdateRequest>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateProductSortimentEntryPrices$Response(
      params,
      context
    ).pipe(map((r: StrictHttpResponse<void>) => r.body as void));
  }

  /**
   * Path part for operation getProductSortimentsForPricing
   */
  static readonly GetProductSortimentsForPricingPath = '/pricing/selected';

  /**
   * Gets list of selected product sortiments.
   *
   * Gets list of selected product sortiments with their entries containing site information and price values.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductSortimentsForPricing()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getProductSortimentsForPricing$Response(
    params: {
      body: ProductSortimentsPricingRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<ProductSortimentsPricingResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PricingService.GetProductSortimentsForPricingPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<
            Array<ProductSortimentsPricingResponse>
          >;
        })
      );
  }

  /**
   * Gets list of selected product sortiments.
   *
   * Gets list of selected product sortiments with their entries containing site information and price values.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductSortimentsForPricing$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getProductSortimentsForPricing(
    params: {
      body: ProductSortimentsPricingRequest;
    },
    context?: HttpContext
  ): Observable<Array<ProductSortimentsPricingResponse>> {
    return this.getProductSortimentsForPricing$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<ProductSortimentsPricingResponse>>) =>
          r.body as Array<ProductSortimentsPricingResponse>
      )
    );
  }

  /**
   * Path part for operation bulkPriceUpdate
   */
  static readonly BulkPriceUpdatePath = '/pricing/bulk-update';

  /**
   * Updates list of prices.
   *
   * The endpoint finds prices to update by merchantId, siteId and by the id of the product (packagingUnit) for the RETAILER segment, then sends back the status and error code if present.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkPriceUpdate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkPriceUpdate$Response(
    params: {
      body: BulkPriceUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BulkPriceUpdateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PricingService.BulkPriceUpdatePath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<BulkPriceUpdateResponse>;
        })
      );
  }

  /**
   * Updates list of prices.
   *
   * The endpoint finds prices to update by merchantId, siteId and by the id of the product (packagingUnit) for the RETAILER segment, then sends back the status and error code if present.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `bulkPriceUpdate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkPriceUpdate(
    params: {
      body: BulkPriceUpdateRequest;
    },
    context?: HttpContext
  ): Observable<BulkPriceUpdateResponse> {
    return this.bulkPriceUpdate$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<BulkPriceUpdateResponse>) =>
          r.body as BulkPriceUpdateResponse
      )
    );
  }

  /**
   * Path part for operation getProductSortimentPricingList
   */
  static readonly GetProductSortimentPricingListPath = '/pricing';

  /**
   * Gets product sortiment pricing list.
   *
   * Finds and returns products sortiment pricing list of logged in merchant. Only product sortiments assigned to at least one site are shown in the list.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductSortimentPricingList()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getProductSortimentPricingList$Response(
    params: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
      body: ProductSortimentPricingFilter;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageProductSortimentPricingPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PricingService.GetProductSortimentPricingListPath,
      'post'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageProductSortimentPricingPageResponse>;
        })
      );
  }

  /**
   * Gets product sortiment pricing list.
   *
   * Finds and returns products sortiment pricing list of logged in merchant. Only product sortiments assigned to at least one site are shown in the list.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductSortimentPricingList$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getProductSortimentPricingList(
    params: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
      body: ProductSortimentPricingFilter;
    },
    context?: HttpContext
  ): Observable<PageProductSortimentPricingPageResponse> {
    return this.getProductSortimentPricingList$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageProductSortimentPricingPageResponse>) =>
          r.body as PageProductSortimentPricingPageResponse
      )
    );
  }

  /**
   * Path part for operation getOriginalPricing
   */
  static readonly GetOriginalPricingPath = '/pricing/original';

  /**
   * Gets original pricing of selected product sortiment for either the discount receiver or the discount giver.
   *
   * Returns only ENABLED ProductSortimentEntries. For discount receivers only ACTIVE Prices are returned that are relevant based on current Merchant's approved MerchantTypes.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOriginalPricing()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOriginalPricing$Response(
    params: {
      productSortimentId: string;
      isRequestedAsDiscountReceiver: boolean;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<OriginalPricingResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PricingService.GetOriginalPricingPath,
      'post'
    );
    if (params) {
      rb.query('productSortimentId', params.productSortimentId, {});
      rb.query(
        'isRequestedAsDiscountReceiver',
        params.isRequestedAsDiscountReceiver,
        {}
      );
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<OriginalPricingResponse>;
        })
      );
  }

  /**
   * Gets original pricing of selected product sortiment for either the discount receiver or the discount giver.
   *
   * Returns only ENABLED ProductSortimentEntries. For discount receivers only ACTIVE Prices are returned that are relevant based on current Merchant's approved MerchantTypes.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOriginalPricing$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOriginalPricing(
    params: {
      productSortimentId: string;
      isRequestedAsDiscountReceiver: boolean;
    },
    context?: HttpContext
  ): Observable<OriginalPricingResponse> {
    return this.getOriginalPricing$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<OriginalPricingResponse>) =>
          r.body as OriginalPricingResponse
      )
    );
  }
}
